import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, FormControl, FormLabel, HStack, IconButton, Input } from "@chakra-ui/react";
import { useTheme } from "next-themes";
import {
  Background,
  Connection,
  Controls,
  Handle,
  MarkerType,
  NodeProps,
  NodeTypes,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  type ReactFlowInstance,
  type EdgeMouseHandler,
  type OnConnect,
  type OnNodeDrag,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useConfiguratorStyleInputProps } from "../../../components/Reports/ConfiguratorStyleMenu";
import {
  TradeFilterSelect,
  TradeFilterSelectFullWidth,
  TradeFilterSearchableSelectFullWidth,
  TradeMultiFilterSelect,
} from "../../../components/trade-filter-selects";
import {
  applyTradeDirectionSelect,
  catalogFieldPickerOptions,
  formatOperatorLabel,
  tradeDirectionSelectValue,
  tradeTypeAxis,
} from "./policyTreeRuleHelpers";
import { policyCatalogFieldDisplayLabel } from "../../../utils/policyCatalogFieldLabels";
import { PolicyRuleNumericValueInputs } from "./policyRuleNumericValue";
import type { TradePolicyCatalogField } from "../../../types/tradePolicy";
import {
  PolicyRuleFieldExplanationBlock,
  type PolicyRuleFieldExplanation,
} from "./policyFieldExplanation";
import { EditIcon } from "../../../components/ui/edit";
import { TrashIcon } from "../../../components/ui/trash";
import { SimulatorInlineEditActions } from "./simulatorInlineEdit";
import { SimulatorPolicyAddButton } from "./SimulatorPolicyAddButton";
import {
  catalogEnumValuesForPicker,
  GROUP_POLICY_ACTION_OPTIONS,
  isPolicyCombineShellGroup,
  type EditorGroupNode,
  type EditorNode,
  type EditorRuleNode,
} from "./policyTreeUtils";
import type { TradePolicyAction } from "../../../types/tradePolicy";
import { getPolicyGraphChrome } from "./policyTreeTheme";

const FormControlAny = FormControl as unknown as React.FC<any>;
const FormLabelAny = FormLabel as unknown as React.FC<any>;
const HStackAny = HStack as unknown as React.FC<any>;
const IconButtonAny = IconButton as unknown as React.FC<any>;
const InputAny = Input as unknown as React.FC<any>;

type Props = {
  roots: EditorNode[];
  catalogFields: TradePolicyCatalogField[];
  catalogFieldsForPicker: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  defaultGroupAction: TradePolicyAction;
  onAddGroupNode: () => void;
  onConnectRuleToGroup: (ruleId: string, groupId: string) => void;
  onDetachConnection: (groupId: string, nodeId: string) => void;
  onRemoveNode: (id: string) => void;
  onUpdateRule: (id: string, patch: Partial<EditorRuleNode>) => void;
  onRulePathChange: (id: string, path: string) => void;
  onRuleOpChange: (id: string, op: string) => void;
  onGroupOpChange: (id: string, op: EditorGroupNode["op"]) => void;
  onGroupActionChange: (id: string, action: TradePolicyAction) => void;
  onGroupLabelChange: (id: string, label: string) => void;
  onAddRuleToGroup: (groupId: string) => void;
  fieldExplanationFor: (field: TradePolicyCatalogField) => PolicyRuleFieldExplanation;
  readOnly?: boolean;
  defaultGroupLabel?: string;
  hideGroupAction?: boolean;
};

function ruleValueLabel(node: EditorRuleNode): string {
  if (node.op === "in") {
    if ((node.valueEnumSet ?? []).length > 0) return node.valueEnumSet.join(", ");
    return node.valueEnum || "—";
  }
  if (node.op === "range") {
    const min = (node as any).valueNumberMin?.trim?.() ?? "";
    const max = (node as any).valueNumberMax?.trim?.() ?? "";
    return `${min || "…"} to ${max || "…"}`;
  }
  if (node.valueNumber?.trim()) return node.valueNumber;
  if (node.valueEnum?.trim()) return node.valueEnum;
  return "—";
}

type Flat = { id: string; node: EditorNode; depth: number; parentId?: string };
type XY = { x: number; y: number };
type LayoutResult = {
  width: number;
  centerX: number;
  positions: Record<string, XY>;
};

const GROUP_NODE_WIDTH = 300;
const RULE_NODE_WIDTH = 340;
const GROUP_NODE_HEIGHT = 280;
const RULE_NODE_HEIGHT = 320;
const GRID_BAR_PX = 20;
const NODE_ROW_GAP_Y = GRID_BAR_PX * 4;
const NODE_GAP_X = GRID_BAR_PX * 2;
const ROOT_GAP_X = 120;
const CANVAS_PADDING_X = 80;
const CANVAS_PADDING_Y = 80;
const FIT_VIEW_PADDING = 0.2;
/**
 * Floor so fitView never shrinks nodes into unreadable dots (was 0.08).
 * Users can still pan when a tall tree does not fit the viewport.
 */
const POLICY_GRAPH_MIN_ZOOM = 0.4;
const POLICY_GRAPH_MAX_ZOOM = 1.5;
/** Prefer readable size when fitting; do not blow up tiny single-rule graphs. */
const POLICY_GRAPH_FIT_MAX_ZOOM = 1;

type GroupNodeData = {
  node: EditorGroupNode;
  defaultGroupAction: TradePolicyAction;
  onRemoveNode: (id: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: (id: string) => void;
  onGroupOpChange: (id: string, op: EditorGroupNode["op"]) => void;
  onGroupActionChange: (id: string, action: TradePolicyAction) => void;
  onGroupLabelChange: (id: string, label: string) => void;
  onAddRuleToGroup: (groupId: string) => void;
  border: string;
  nodeBg: string;
  text: string;
  subtle: string;
  readOnly?: boolean;
  defaultGroupLabel?: string;
  hideGroupAction?: boolean;
};

type RuleNodeData = {
  node: EditorRuleNode;
  onRemoveNode: (id: string) => void;
  catalogFieldsForPicker: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  onUpdateRule: (id: string, patch: Partial<EditorRuleNode>) => void;
  onRulePathChange: (id: string, path: string) => void;
  onRuleOpChange: (id: string, op: string) => void;
  border: string;
  ruleBg: string;
  text: string;
  subtle: string;
  fieldExplanation: PolicyRuleFieldExplanation | null;
  readOnly?: boolean;
};

function GroupNode(props: NodeProps): React.ReactElement {
  const data = props.data as GroupNodeData;
  const readOnly = Boolean(data.readOnly);
  const groupStroke = "#3EEAEC";
  const groupTitle = "#38BBD1";
  const groupLabelEditRef = useRef<HTMLDivElement | null>(null);
  const [isEditingGroupLabel, setIsEditingGroupLabel] = useState(false);
  const [groupLabelDraft, setGroupLabelDraft] = useState(data.node.label ?? "");

  useEffect(() => {
    if (!isEditingGroupLabel) setGroupLabelDraft(data.node.label ?? "");
  }, [data.node.label, isEditingGroupLabel]);

  const groupLabel = (() => {
    const label = (data.node.label ?? "").trim();
    if (label && label !== "Group") return label;
    if (isPolicyCombineShellGroup(data.node)) return "Combined policies";
    return data.defaultGroupLabel?.trim() || "Group";
  })();
  const hideActionForNode = Boolean(data.hideGroupAction) || isPolicyCombineShellGroup(data.node);

  const endGroupLabelEdit = () => {
    setGroupLabelDraft(data.node.label ?? "");
    setIsEditingGroupLabel(false);
  };

  const commitGroupLabelEdit = () => {
    data.onGroupLabelChange(data.node.id, groupLabelDraft.trim());
    setIsEditingGroupLabel(false);
  };

  const onGroupLabelBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isEditingGroupLabel) return;
    const next = e.relatedTarget;
    if (next instanceof Node && groupLabelEditRef.current?.contains(next)) return;
    endGroupLabelEdit();
  };

  return (
    <Box
      bg={data.nodeBg}
      border={`1px solid ${groupStroke}`}
      borderRadius="10px"
      w={`${GROUP_NODE_WIDTH}px`}
      maxW={`${GROUP_NODE_WIDTH}px`}
      p={3}
      position="relative"
      overflow="visible"
    >
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} />
      <Box display="flex" flexDirection="column" gap="8px">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
              color: groupTitle,
              fontFamily: "'Urbanist', sans-serif",
              fontWeight: 600,
            }}
          >
            Group
          </div>
          {!readOnly ? (
            <IconButtonAny
              aria-label="Delete group"
              icon={<TrashIcon size={16} aria-hidden />}
              size="xs"
              variant="ghost"
              color="red.400"
              className="nodrag"
              onClick={() => data.onRemoveNode(data.node.id)}
            />
          ) : null}
        </Box>
        {isEditingGroupLabel && !readOnly ? (
          <HStackAny ref={groupLabelEditRef} className="nodrag" spacing={2} align="flex-end" flexWrap="nowrap" w="100%">
            <InputAny
              className="nodrag"
              size="xs"
              flex="1 1 auto"
              minW={0}
              maxW="160px"
              value={groupLabelDraft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupLabelDraft(e.target.value)}
              onBlur={onGroupLabelBlur}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitGroupLabelEdit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  endGroupLabelEdit();
                }
              }}
              autoFocus
              placeholder="Group name"
            />
            <SimulatorInlineEditActions onSave={commitGroupLabelEdit} onCancel={endGroupLabelEdit} />
          </HStackAny>
        ) : (
          <Box display="flex" alignItems="center" gap="4px">
            <Box as="span" fontSize="sm" fontWeight="semibold" color={groupTitle} fontFamily="'Urbanist', sans-serif">
              {groupLabel}
            </Box>
            {!readOnly ? (
              <IconButtonAny
                aria-label="Edit group title"
                icon={<EditIcon size={16} aria-hidden />}
                size="xs"
                variant="ghost"
                color={data.subtle}
                className="nodrag"
                onClick={() => {
                  setGroupLabelDraft(data.node.label ?? "");
                  setIsEditingGroupLabel(true);
                }}
              />
            ) : null}
          </Box>
        )}
        {!hideActionForNode ? (
          <FormControlAny>
            <FormLabelAny fontSize="10px" color={data.subtle} mb={1}>
              Action
            </FormLabelAny>
            <Box className="nodrag w-fit min-w-0">
              <TradeFilterSelect
                layout="fit"
                fitToLongestOption
                value={data.node.action ?? data.defaultGroupAction}
                onValueChange={(value) => data.onGroupActionChange(data.node.id, value as TradePolicyAction)}
                options={GROUP_POLICY_ACTION_OPTIONS}
                disabled={readOnly}
              />
            </Box>
          </FormControlAny>
        ) : null}
        <FormControlAny>
          <FormLabelAny fontSize="10px" color={data.subtle} mb={1}>
            Match
          </FormLabelAny>
          <Box className="nodrag" minW="10rem">
            <TradeFilterSelectFullWidth
              value={data.node.op}
              onValueChange={(value) => data.onGroupOpChange(data.node.id, value as EditorGroupNode["op"])}
              options={[
                { value: "and", label: "All of (AND)" },
                { value: "or", label: "Any of (OR)" },
              ]}
              disabled={readOnly}
            />
          </Box>
        </FormControlAny>
        {!readOnly && !hideActionForNode ? (
          <Box className="nodrag">
            <SimulatorPolicyAddButton kind="rule" onClick={() => data.onAddRuleToGroup(data.node.id)} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function RuleNode(props: NodeProps): React.ReactElement {
  const data = props.data as RuleNodeData;
  const readOnly = Boolean(data.readOnly);
  const configuratorInputProps = useConfiguratorStyleInputProps();
  const ruleStroke = "#F4B400";
  const ruleTitle = "#F4B400";
  const field = data.fieldsByPath.get(data.node.path);
  const fieldDisplayLabel = field
    ? policyCatalogFieldDisplayLabel(data.node.path, field.label)
    : data.node.path.trim()
      ? policyCatalogFieldDisplayLabel(data.node.path)
      : "Select field";
  const ops = [...(field?.allowed_ops ?? [])];
  if (field?.value_kind === "number") {
    const canRange = ops.includes("gte") || ops.includes("lte");
    if (canRange) ops.unshift("range");
  }
  const isEnum = field?.value_kind === "enum";
  const isIn = data.node.op === "in";
  const typeAxis = field ? tradeTypeAxis(field) : null;
  const isTradeTypeDirection = Boolean(typeAxis);
  const enumPickerValues = catalogEnumValuesForPicker(field?.enum_values, [
    data.node.valueEnum,
    ...(data.node.valueEnumSet ?? []),
  ]);
  return (
    <Box
      bg={data.ruleBg}
      border={`1px solid ${ruleStroke}`}
      borderRadius="10px"
      w={`${RULE_NODE_WIDTH}px`}
      maxW={`${RULE_NODE_WIDTH}px`}
      p={3}
    >
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      <Box display="flex" flexDirection="column" gap="8px" w="100%" minW={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
              color: ruleTitle,
              fontFamily: "'Urbanist', sans-serif",
              fontWeight: 600,
            }}
          >
            {`Rule | ${fieldDisplayLabel}`}
          </div>
          {!readOnly ? (
            <IconButtonAny
              aria-label="Delete rule"
              icon={<TrashIcon size={16} aria-hidden />}
              size="xs"
              variant="ghost"
              color="red.400"
              className="nodrag"
              onClick={() => data.onRemoveNode(data.node.id)}
            />
          ) : null}
        </Box>
        <FormControlAny minW={0} w="100%">
          <FormLabelAny fontSize="10px" color={data.subtle} mb={1}>
            Field
          </FormLabelAny>
          <Box className="nodrag" w="100%" minW={0}>
            <TradeFilterSearchableSelectFullWidth
              value={data.node.path}
              onValueChange={(value) => data.onRulePathChange(data.node.id, value)}
              options={catalogFieldPickerOptions(data.catalogFieldsForPicker, data.node.path, data.fieldsByPath)}
              placeholder="Search fields…"
              aria-label="Field"
              disabled={readOnly}
            />
          </Box>
        </FormControlAny>
        {isTradeTypeDirection && typeAxis ? (
          <FormControlAny minW={0} w="100%">
            <FormLabelAny fontSize="10px" color={data.subtle} mb={1}>
              Direction
            </FormLabelAny>
            <Box className="nodrag" w="100%" minW={0}>
              <TradeFilterSelectFullWidth
                value={tradeDirectionSelectValue(data.node, typeAxis)}
                onValueChange={(value) =>
                  applyTradeDirectionSelect(data.node.id, value, typeAxis, data.onUpdateRule)
                }
                options={[
                  { value: "", label: "Select" },
                  { value: "long", label: "Long" },
                  { value: "short", label: "Short" },
                  { value: "both", label: "Both", disabled: !field?.allowed_ops.includes("in") },
                ]}
                placeholder="Select"
                disabled={readOnly}
              />
            </Box>
          </FormControlAny>
        ) : (
          <Box display="flex" gap="8px" w="100%" minW={0} alignItems="flex-start">
            <FormControlAny flex="1" minW={0}>
              <FormLabelAny fontSize="10px" color={data.subtle} mb={1}>
                Operator
              </FormLabelAny>
              <Box className="nodrag" w="100%" minW={0}>
                <TradeFilterSelectFullWidth
                  value={data.node.op}
                  onValueChange={(value) => data.onRuleOpChange(data.node.id, value)}
                  options={Array.from(new Set(ops)).map((op) => ({
                    value: op,
                    label: formatOperatorLabel(op),
                  }))}
                  placeholder="Select operator"
                  disabled={readOnly}
                />
              </Box>
            </FormControlAny>
            <FormControlAny flex="1" minW={0}>
              <FormLabelAny fontSize="10px" color={data.subtle} mb={1}>
                Value
              </FormLabelAny>
              {field?.value_kind === "number" ? (
                <Box className="nodrag">
                  <PolicyRuleNumericValueInputs
                    field={field}
                    op={data.node.op}
                    value={data.node.valueNumber}
                    valueMin={data.node.valueNumberMin ?? ""}
                    valueMax={data.node.valueNumberMax ?? ""}
                    onValueChange={(value) => data.onUpdateRule(data.node.id, { valueNumber: value })}
                    onValueMinChange={(value) => data.onUpdateRule(data.node.id, { valueNumberMin: value })}
                    onValueMaxChange={(value) => data.onUpdateRule(data.node.id, { valueNumberMax: value })}
                    readOnly={readOnly}
                  />
                </Box>
              ) : isEnum && isIn && enumPickerValues.length > 0 ? (
                <Box className={readOnly ? "nodrag pointer-events-none opacity-80 w-full min-w-0" : "nodrag w-full min-w-0"}>
                  <TradeMultiFilterSelect
                    layout="full"
                    options={enumPickerValues}
                    selected={(() => {
                      const set = data.node.valueEnumSet ?? [];
                      if (set.length > 0) return set;
                      const draft = data.node.valueEnum?.trim() ?? "";
                      if (!draft) return [];
                      return draft
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                    })()}
                    emptyLabel="Select..."
                    onToggle={(token) => {
                      const set = data.node.valueEnumSet ?? [];
                      const fromDraft =
                        set.length > 0
                          ? set
                          : (data.node.valueEnum?.trim() ?? "")
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean);
                      const next = fromDraft.includes(token)
                        ? fromDraft.filter((t) => t !== token)
                        : [...fromDraft, token];
                      data.onUpdateRule(data.node.id, { valueEnum: "", valueEnumSet: next });
                    }}
                  />
                </Box>
              ) : isEnum && !isIn && enumPickerValues.length > 0 ? (
                <Box className="nodrag" w="100%" minW={0}>
                  <TradeFilterSelectFullWidth
                    value={data.node.valueEnum}
                    onValueChange={(value) =>
                      data.onUpdateRule(data.node.id, { valueEnum: value, valueEnumSet: [] })
                    }
                    options={enumPickerValues.map((token) => ({ value: token, label: token }))}
                    placeholder="Select..."
                    disabled={readOnly}
                  />
                </Box>
              ) : isEnum ? (
                <InputAny
                  className="nodrag"
                  size="xs"
                  placeholder="value"
                  value={data.node.valueEnum}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    data.onUpdateRule(data.node.id, { valueEnum: e.target.value, valueEnumSet: [] })
                  }
                  isDisabled={readOnly}
                />
              ) : null}
            </FormControlAny>
          </Box>
        )}
        {data.fieldExplanation ? (
          <PolicyRuleFieldExplanationBlock
            compact
            explanation={data.fieldExplanation}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function flatten(
  nodes: EditorNode[],
  depth = 0,
  parentId?: string,
  out: Flat[] = [],
  collapsedGroupIds: Set<string> = new Set<string>()
): Flat[] {
  for (const n of nodes) {
    out.push({ id: n.id, node: n, depth, parentId });
    if (n.kind === "group" && n.children.length && !collapsedGroupIds.has(n.id)) {
      flatten(n.children, depth + 1, n.id, out, collapsedGroupIds);
    }
  }
  return out;
}

function nodeWidth(node: EditorNode): number {
  return node.kind === "group" ? GROUP_NODE_WIDTH : RULE_NODE_WIDTH;
}

function nodeHeight(node: EditorNode): number {
  return node.kind === "group" ? GROUP_NODE_HEIGHT : RULE_NODE_HEIGHT;
}

function layoutNode(node: EditorNode, collapsedGroupIds: Set<string>): LayoutResult {
  const selfWidth = nodeWidth(node);
  const isCollapsedGroup = node.kind === "group" && collapsedGroupIds.has(node.id);
  if (node.kind === "rule" || node.children.length === 0 || isCollapsedGroup) {
    return {
      width: selfWidth,
      centerX: selfWidth / 2,
      positions: {
        [node.id]: { x: 0, y: 0 },
      },
    };
  }

  const childLayouts = node.children.map((child) => layoutNode(child, collapsedGroupIds));
  const childrenTotalWidth =
    childLayouts.reduce((sum, child) => sum + child.width, 0) + NODE_GAP_X * Math.max(0, childLayouts.length - 1);
  const totalWidth = Math.max(selfWidth, childrenTotalWidth);
  const parentCenterX = totalWidth / 2;
  const childrenStartX = (totalWidth - childrenTotalWidth) / 2;

  const positions: Record<string, XY> = {
    [node.id]: { x: parentCenterX - selfWidth / 2, y: 0 },
  };

  let cursorX = childrenStartX;
  for (const child of childLayouts) {
    const offsetX = cursorX;
    const childYOffset = nodeHeight(node) + NODE_ROW_GAP_Y;
    for (const [id, pos] of Object.entries(child.positions)) {
      positions[id] = { x: pos.x + offsetX, y: pos.y + childYOffset };
    }
    cursorX += child.width + NODE_GAP_X;
  }

  return {
    width: totalWidth,
    centerX: parentCenterX,
    positions,
  };
}

function layoutRoots(roots: EditorNode[], collapsedGroupIds: Set<string>): Record<string, XY> {
  const rootLayouts = roots.map((root) => layoutNode(root, collapsedGroupIds));
  const out: Record<string, XY> = {};
  let cursorX = CANVAS_PADDING_X;
  for (const layout of rootLayouts) {
    for (const [id, pos] of Object.entries(layout.positions)) {
      out[id] = { x: pos.x + cursorX, y: pos.y + CANVAS_PADDING_Y };
    }
    cursorX += layout.width + ROOT_GAP_X;
  }
  return out;
}

export function PolicyGraphCanvas({
  roots,
  catalogFields: _catalogFields,
  catalogFieldsForPicker,
  fieldsByPath,
  defaultGroupAction,
  onAddGroupNode,
  onConnectRuleToGroup,
  onDetachConnection,
  onRemoveNode,
  onUpdateRule,
  onRulePathChange,
  onRuleOpChange,
  onGroupOpChange,
  onGroupActionChange,
  onGroupLabelChange,
  onAddRuleToGroup,
  fieldExplanationFor,
  readOnly = false,
  defaultGroupLabel,
  hideGroupAction = false,
}: Props): React.ReactElement {
  const { resolvedTheme, forcedTheme } = useTheme();
  const isDarkTheme = useMemo(() => {
    const theme = forcedTheme ?? resolvedTheme;
    if (theme === "dark") return true;
    if (theme === "light") return false;
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      if (root.classList.contains("dark") || root.getAttribute("data-theme") === "dark") {
        return true;
      }
    }
    return false;
  }, [forcedTheme, resolvedTheme]);
  const graphChrome = useMemo(() => getPolicyGraphChrome(isDarkTheme), [isDarkTheme]);
  const nodeBg = graphChrome.groupNodeBg;
  const ruleBg = graphChrome.ruleNodeBg;
  const border = graphChrome.border;
  const text = graphChrome.text;
  const subtle = graphChrome.subtle;
  const [positionsById, setPositionsById] = useState<Record<string, XY>>({});
  const [draggedNodeIds, setDraggedNodeIds] = useState<Record<string, true>>({});
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Record<string, true>>({});
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance | null>(null);
  const lastStructureSignatureRef = useRef<string>("");
  const didInitialFitRef = useRef(false);
  const structureSignature = useMemo(() => {
    const links: string[] = [];
    const walk = (node: EditorNode, parentId: string | null) => {
      links.push(`${node.id}:${node.kind}:${parentId ?? "root"}`);
      if (node.kind === "group") node.children.forEach((child) => walk(child, node.id));
    };
    roots.forEach((root) => walk(root, null));
    return links.sort().join("|");
  }, [roots]);
  const collapsedSet = useMemo(() => new Set(Object.keys(collapsedGroupIds)), [collapsedGroupIds]);
  const collapsedSignature = useMemo(() => Object.keys(collapsedGroupIds).sort().join("|"), [collapsedGroupIds]);
  const toggleCollapsedGroup = useCallback((id: string) => {
    setCollapsedGroupIds((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  }, []);

  const rootsRef = useRef(roots);
  rootsRef.current = roots;
  const draggedNodeIdsRef = useRef(draggedNodeIds);
  draggedNodeIdsRef.current = draggedNodeIds;

  useEffect(() => {
    const autoLayout = layoutRoots(rootsRef.current, collapsedSet);
    const structureChanged = lastStructureSignatureRef.current !== structureSignature;
    lastStructureSignatureRef.current = structureSignature;
    if (structureChanged) {
      setDraggedNodeIds({});
      setPositionsById(autoLayout);
      return;
    }
    const dragged = draggedNodeIdsRef.current;
    setPositionsById((prev) => {
      let changed = false;
      const next: Record<string, XY> = {};
      for (const id of Object.keys(autoLayout)) {
        const pos = dragged[id] ? (prev[id] ?? autoLayout[id]!) : autoLayout[id]!;
        next[id] = pos;
        const prevPos = prev[id];
        if (!prevPos || prevPos.x !== pos.x || prevPos.y !== pos.y) changed = true;
      }
      if (Object.keys(prev).length !== Object.keys(next).length) changed = true;
      return changed ? next : prev;
    });
    // Only re-layout when tree structure / collapse changes — not when the parent
    // re-renders (e.g. typing the run title) with a new `roots` array identity.
  }, [structureSignature, collapsedSignature, collapsedSet]);

  const fitPolicyGraph = useCallback(
    (duration = 200) => {
      if (!flowInstance) return;
      flowInstance.fitView({
        padding: FIT_VIEW_PADDING,
        duration,
        minZoom: POLICY_GRAPH_MIN_ZOOM,
        maxZoom: POLICY_GRAPH_FIT_MAX_ZOOM,
      });
    },
    [flowInstance]
  );

  useEffect(() => {
    if (!flowInstance) return;
    didInitialFitRef.current = false;
    const t1 = window.setTimeout(() => {
      fitPolicyGraph(250);
      didInitialFitRef.current = true;
    }, 0);
    const t2 = window.setTimeout(() => fitPolicyGraph(0), 180);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // Re-fit on structure / collapse only — not on every position tick (that fought zoom).
  }, [fitPolicyGraph, flowInstance, structureSignature, collapsedSignature]);

  const canvasHostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = canvasHostRef.current;
    if (!host || !flowInstance || typeof ResizeObserver === "undefined") return;
    let timer = 0;
    let lastW = 0;
    let lastH = 0;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect || rect.width < 8 || rect.height < 8) return;
      if (Math.abs(rect.width - lastW) < 40 && Math.abs(rect.height - lastH) < 40) return;
      lastW = rect.width;
      lastH = rect.height;
      // Skip the first paint before initial fit settles.
      if (!didInitialFitRef.current) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fitPolicyGraph(0), 100);
    });
    observer.observe(host);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [fitPolicyGraph, flowInstance]);

  const { nodes, edges, kindById } = useMemo(() => {
    const flat = flatten(roots, 0, undefined, [], collapsedSet);
    const byDepth = new Map<number, Flat[]>();
    const outKindById = new Map<string, EditorNode["kind"]>();
    for (const item of flat) {
      const arr = byDepth.get(item.depth) ?? [];
      arr.push(item);
      byDepth.set(item.depth, arr);
      outKindById.set(item.id, item.node.kind);
    }

    const outNodes: Node[] = [];
    const outEdges: Edge[] = [];
    const xGap = 320;
    const yGap = 140;

    byDepth.forEach((arr, depth) => {
      arr.forEach((item: Flat, idx: number) => {
        const n = item.node;
        outNodes.push({
          id: item.id,
          position: positionsById[item.id] ?? { x: depth * xGap, y: idx * yGap },
          type: n.kind === "group" ? "groupNode" : "ruleNode",
          data:
            n.kind === "group"
              ? {
                  node: n,
                  defaultGroupAction,
                  onRemoveNode,
                  isCollapsed: Boolean(collapsedGroupIds[n.id]),
                  onToggleCollapse: toggleCollapsedGroup,
                  onGroupOpChange,
                  onGroupActionChange,
                  onGroupLabelChange,
                  onAddRuleToGroup,
                  border,
                  nodeBg,
                  text,
                  subtle,
                  readOnly,
                  defaultGroupLabel: depth === 0 ? defaultGroupLabel : undefined,
                  hideGroupAction,
                }
              : {
                  node: n,
                  onRemoveNode,
                  catalogFieldsForPicker,
                  fieldsByPath,
                  onUpdateRule,
                  onRulePathChange,
                  onRuleOpChange,
                  border,
                  ruleBg,
                  text,
                  subtle,
                  readOnly,
                  fieldExplanation: (() => {
                    if (n.kind !== "rule" || !n.path.trim()) return null;
                    const f = fieldsByPath.get(n.path);
                    return f ? fieldExplanationFor(f) : null;
                  })(),
                },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
          draggable: !readOnly,
        });

        if (item.parentId) {
          const edgeId = `e:${item.parentId}->${item.id}`;
          const sourceIsGroup = outKindById.get(item.parentId) === "group";
          const isHovered = hoveredEdgeId === edgeId;
          outEdges.push({
            id: edgeId,
            source: item.parentId,
            target: item.id,
            type: "straight",
            markerEnd: { type: MarkerType.ArrowClosed, color: graphChrome.edgeStroke },
            style: {
              stroke: isHovered ? "#4299E1" : graphChrome.edgeStroke,
              strokeWidth: isHovered ? 2.25 : 1.5,
              cursor: !readOnly && sourceIsGroup ? "pointer" : "default",
            },
          });
        }
      });
    });

    return { nodes: outNodes, edges: outEdges, kindById: outKindById };
  }, [
    roots,
    collapsedSet,
    collapsedGroupIds,
    catalogFieldsForPicker,
    defaultGroupAction,
    fieldsByPath,
    onUpdateRule,
    onRulePathChange,
    onRuleOpChange,
    onRemoveNode,
    onGroupOpChange,
    onGroupActionChange,
    onGroupLabelChange,
    onAddRuleToGroup,
    toggleCollapsedGroup,
    border,
    nodeBg,
    ruleBg,
    text,
    subtle,
    positionsById,
    hoveredEdgeId,
    fieldExplanationFor,
    readOnly,
    defaultGroupLabel,
    hideGroupAction,
    graphChrome.edgeStroke,
  ]);

  const nodeTypes = useMemo<NodeTypes>(
    () => ({
      groupNode: GroupNode,
      ruleNode: RuleNode,
    }),
    []
  );

  const handleConnect = useCallback<OnConnect>(
    (params: Connection) => {
      const source = params.source ?? "";
      const target = params.target ?? "";
      if (!source || !target || source === target) return;
      const sourceKind = kindById.get(source);
      const targetKind = kindById.get(target);
      if (sourceKind === "rule" && targetKind === "group") onConnectRuleToGroup(source, target);
      else if (sourceKind === "group" && targetKind === "rule") onConnectRuleToGroup(target, source);
    },
    [kindById, onConnectRuleToGroup]
  );

  const handleEdgeClick = useCallback<React.MouseEventHandler>(
    (event) => {
      event.preventDefault();
    },
    []
  );
  const onEdgeClick: EdgeMouseHandler = useCallback(
    (event, edge) => {
      event.preventDefault();
      if (readOnly) return;
      if (kindById.get(edge.source) === "group") onDetachConnection(edge.source, edge.target);
    },
    [kindById, onDetachConnection, readOnly]
  );
  const onEdgeMouseEnter: EdgeMouseHandler = useCallback((_event, edge) => setHoveredEdgeId(edge.id), []);
  const onEdgeMouseLeave: EdgeMouseHandler = useCallback(() => setHoveredEdgeId(null), []);

  const updateNodePosition = useCallback((node: Node) => {
    setDraggedNodeIds((prev) => (prev[node.id] ? prev : { ...prev, [node.id]: true }));
    setPositionsById((prev) => ({
      ...prev,
      [node.id]: { x: node.position.x, y: node.position.y },
    }));
  }, []);

  const handleNodeDrag: OnNodeDrag<Node> = useCallback((_event, node) => {
    updateNodePosition(node);
  }, [updateNodePosition]);

  const handleNodeDragStop: OnNodeDrag<Node> = useCallback((_event, node) => {
    updateNodePosition(node);
  }, [updateNodePosition]);

  return (
    <Box
      ref={canvasHostRef}
      data-policy-graph-canvas=""
      borderWidth="1px"
      borderColor={border}
      borderRadius="12px"
      overflow="hidden"
      // Keep controls on-screen in short wizard dialogs; grow when space allows.
      h="clamp(280px, calc(92vh - 340px), 560px)"
      minH="280px"
      bg={graphChrome.canvasBg}
      position="relative"
    >
      {/* Isolate store so multiple policy graphs (and the workspace canvas) do not share nodes. */}
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          proOptions={{ hideAttribution: true }}
          nodeTypes={nodeTypes}
          // Controlled fit only (structure/collapse/resize) — avoid remount fitView
          // churn when the parent re-renders (e.g. typing Next run title).
          fitViewOptions={{
            padding: FIT_VIEW_PADDING,
            minZoom: POLICY_GRAPH_MIN_ZOOM,
            maxZoom: POLICY_GRAPH_FIT_MAX_ZOOM,
          }}
          minZoom={POLICY_GRAPH_MIN_ZOOM}
          maxZoom={POLICY_GRAPH_MAX_ZOOM}
          zoomOnScroll
          panOnScroll={false}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          style={{ background: graphChrome.canvasBg, width: "100%", height: "100%" }}
          onConnect={readOnly ? undefined : handleConnect}
          onClick={handleEdgeClick}
          onEdgeClick={readOnly ? undefined : onEdgeClick}
          onEdgeMouseEnter={readOnly ? undefined : onEdgeMouseEnter}
          onEdgeMouseLeave={readOnly ? undefined : onEdgeMouseLeave}
          onNodeDrag={readOnly ? undefined : handleNodeDrag}
          onNodeDragStop={readOnly ? undefined : handleNodeDragStop}
          onInit={setFlowInstance}
        >
          <Controls position="top-left" showInteractive={false} />
          {!readOnly ? (
            <Panel position="top-right">
              <Box display="flex" gap="8px" className="nodrag">
                <SimulatorPolicyAddButton kind="group" onClick={onAddGroupNode} />
              </Box>
            </Panel>
          ) : null}
          <Background gap={GRID_BAR_PX} size={1} color={graphChrome.gridColor} bgColor={graphChrome.canvasBg} />
        </ReactFlow>
      </ReactFlowProvider>
    </Box>
  );
}

